import { apiGet, apiPost, apiPut, apiDelete } from './client';

export interface CreateAdminAccountData {
  companyName: string;
  email: string;
  password: string;
  contactPerson: string;
  phone: string;
  address: string;
  website?: string;
  description?: string;
  logo?: string;
  plan: 'BASIC' | 'PREMIUM' | 'ENTERPRISE';
  maxEvents: number;
  maxTicketsPerEvent: number;
  subscriptionEndDate: string;
}

export interface UpdateAdminAccountData {
  companyName?: string;
  contactPerson?: string;
  phone?: string;
  address?: string;
  website?: string;
  description?: string;
  logo?: string;
  password?: string;
  isActive?: boolean;
  plan?: 'BASIC' | 'PREMIUM' | 'ENTERPRISE';
  maxEvents?: number;
  maxTicketsPerEvent?: number;
  subscriptionEndDate?: string;
}

export interface AdminAccount {
  id: string;
  companyName: string;
  email: string;
  contactPerson: string;
  phone: string;
  address: string;
  website?: string;
  description?: string;
  logo?: string;
  isActive: boolean;
  plan: 'BASIC' | 'PREMIUM' | 'ENTERPRISE';
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  maxEvents: number;
  maxTicketsPerEvent: number;
  currency: string;
  timezone: string;
  language: string;
  allowOnlinePayments: boolean;
  requireApproval: boolean;
  emailNotifications: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMasterUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  masterRole?: 'PLATFORM_OWNER' | 'OPERATIONS_OFFICER' | 'BUSINESS_MANAGER';
}

export interface MasterUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'MASTER';
  isMaster: boolean;
  masterRole?: 'PLATFORM_OWNER' | 'OPERATIONS_OFFICER' | 'BUSINESS_MANAGER';
  createdAt: string;
  updatedAt: string;
}

export const adminAccountsApi = {
  // Get all admin accounts
  getAll: async (params?: { status?: string; plan?: string; page?: number; limit?: number }): Promise<{ data: AdminAccount[]; total: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.plan) queryParams.append('plan', params.plan);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const query = queryParams.toString();
    const response = await apiGet<{ success: boolean; data: { data: AdminAccount[]; total: number }; message?: string }>(`/admin-accounts${query ? `?${query}` : ''}`);
    return response.data || { data: [], total: 0 };
  },

  // Get single admin account
  getById: async (id: string): Promise<AdminAccount> => {
    const response = await apiGet<{ success: boolean; data: AdminAccount; message?: string }>(`/admin-accounts/${id}`);
    return response.data;
  },

  // Create admin account
  create: async (data: CreateAdminAccountData): Promise<AdminAccount> => {
    const response = await apiPost<{ success: boolean; data: AdminAccount; message?: string }>('/admin-accounts', data);
    return response.data;
  },

  // Update admin account
  update: async (id: string, data: UpdateAdminAccountData): Promise<AdminAccount> => {
    const response = await apiPut<{ success: boolean; data: AdminAccount; message?: string }>(`/admin-accounts/${id}`, data);
    return response.data;
  },

  // Delete admin account
  delete: async (id: string): Promise<void> => {
    await apiDelete(`/admin-accounts/${id}`);
  },

  // Toggle admin account status
  toggleStatus: async (id: string, isActive: boolean): Promise<AdminAccount> => {
    const response = await apiPut<{ success: boolean; data: AdminAccount; message?: string }>(`/admin-accounts/${id}`, { isActive });
    return response.data;
  },
};

export const masterUsersApi = {
  // Get all master users
  getAll: async (): Promise<MasterUser[]> => {
    const response = await apiGet<{ success: boolean; data: MasterUser[]; message?: string }>('/master-users');
    return response.data || [];
  },

  // Create master user
  create: async (data: CreateMasterUserData): Promise<MasterUser> => {
    const response = await apiPost<{ success: boolean; data: MasterUser; message?: string }>('/master-users', data);
    return response.data;
  },

  // Update master user
  update: async (id: string, data: Partial<CreateMasterUserData>): Promise<MasterUser> => {
    const response = await apiPut<{ success: boolean; data: MasterUser; message?: string }>(`/master-users/${id}`, data);
    return response.data;
  },

  // Delete master user
  delete: async (id: string): Promise<void> => {
    await apiDelete(`/master-users/${id}`);
  },
};

